import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import config from 'config';
import {
  DonorListFilterInput,
  DonorListSort,
  Order,
  useGetDonorsListQuery,
} from 'gql/types';
import React, { useEffect, useMemo, useState } from 'react';

import { getColumns } from './columns';

import {
  DOUBLE_DONOR_ML_THRESHOLD,
  DonorStatusFilter,
  THREE_MONTHS_MS,
  FILTER_CONFIG,
} from '../../constants';
import { IDonorTableRow } from '../../types';

interface IProps {
  selectedFilter: DonorStatusFilter | null;
  locationFilter: string[] | undefined;
  onDonorClick: (donor: IDonorTableRow) => void;
}

function buildFilter(
  filter: DonorStatusFilter,
  locationFilter: string[] | undefined
): DonorListFilterInput {
  const result: DonorListFilterInput = {};

  if (locationFilter?.length) result.locations = locationFilter;

  result.statuses = FILTER_CONFIG[filter].donorStatuses;

  if (filter === DonorStatusFilter.SingleDonors) {
    result.maxApprovedDonationML = DOUBLE_DONOR_ML_THRESHOLD;
  } else if (filter === DonorStatusFilter.DoubleDonors) {
    result.minApprovedDonationML = DOUBLE_DONOR_ML_THRESHOLD;
  } else if (filter === DonorStatusFilter.NewDonors) {
    result.createdAtFrom = new Date(Date.now() - THREE_MONTHS_MS).toISOString();
  } else if (filter === DonorStatusFilter.ExperiencedDonors) {
    result.createdAtTo = new Date(Date.now() - THREE_MONTHS_MS).toISOString();
  }

  return result;
}

const DonorTable: React.FC<IProps> = ({
  selectedFilter,
  locationFilter,
  onDonorClick,
}) => {
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = config.PAGINATION.PAGE_SIZE;
  const offset = (current - 1) * pageSize;

  const paginationConfig = useMemo(
    (): TablePaginationConfig => ({
      current,
      pageSize,
      total,
      onChange: (page) => setCurrent(page),
      showSizeChanger: false,
      hideOnSinglePage: true,
    }),
    [current, pageSize, total]
  );

  const queryFilter = useMemo((): DonorListFilterInput => {
    if (!selectedFilter) return {};
    return buildFilter(selectedFilter, locationFilter);
  }, [selectedFilter, locationFilter]);

  const { data, loading } = useGetDonorsListQuery({
    variables: {
      input: {
        filter: queryFilter,
        skip: offset,
        take: pageSize,
        sort: [{ fieldName: DonorListSort.Name, order: Order.Asc }],
      },
    },
    skip: !selectedFilter,
    onCompleted: (result) => {
      setTotal(result.getDonorsList.meta.total);
    },
  });

  const donorsData = useMemo(
    (): IDonorTableRow[] =>
      (data?.getDonorsList?.data ?? []).map((d) => ({
        key: d.id,
        id: d.id,
        name: d.name,
        age: d.age,
        gender: d.gender,
        species: d.species,
        weight: d.weight,
        location: d.location,
        status: d.status,
        bloodType: d.bloodType,
        approvedDonationML: d.approvedDonationML,
        createdAt: d.createdAt,
      })),
    [data]
  );

  useEffect(() => {
    setCurrent(1);
  }, [selectedFilter]);

  return (
    <Table
      dataSource={donorsData}
      columns={getColumns(onDonorClick)}
      loading={loading}
      pagination={{ ...paginationConfig, showQuickJumper: true }}
      size="small"
      scroll={{ x: 800 }}
    />
  );
};

export default DonorTable;
